// Chat top-level UI strings — ChatPage header/composer/footer, the sign-in
// page, and the reauth banner. (chat/render/* card copy lives elsewhere.)

import { PUBLIC_PRODUCT_NAME } from "@loom/shared/brand";

import type { MsgSection } from "./types.js";

export const chatTop: MsgSection = {
  // --- BridgeBadge status text (fallback when no server-supplied message) ---
  "chatTop.bridge.pending": { en: "Project preparing…", de: "Projekt wird vorbereitet…" },
  "chatTop.bridge.cloning": {
    en: "Preparing project (copying files…)",
    de: "Projekt wird vorbereitet (Dateien werden kopiert…)",
  },
  "chatTop.bridge.ready": { en: "Project ready", de: "Projekt bereit" },
  "chatTop.bridge.terminated": { en: "Project stopped.", de: "Projekt gestoppt." },
  "chatTop.bridge.error": { en: "Couldn't prepare your project", de: "Dein Projekt konnte nicht vorbereitet werden" },
  "chatTop.resume": { en: "Resume", de: "Fortsetzen" },
  "chatTop.resuming": { en: "Resuming…", de: "Läuft weiter…" },

  // --- Human-idle kill dialog (countdown → hard close to free the sandbox) ---
  "chatTop.idle.title": { en: "Are you still there?", de: "Bist du noch da?" },
  "chatTop.idle.body": {
    en: "You've been inactive for a while. We'll close this session soon unless you let us know you're still here.",
    de: "Du warst eine Weile inaktiv. Wir schliessen diese Sitzung bald, wenn du dich nicht meldest.",
  },
  "chatTop.idle.stay": { en: "I'm still here", de: "Ich bin noch da" },
  "chatTop.idle.killedTitle": {
    en: "Session closed",
    de: "Sitzung geschlossen",
  },
  // Says what was actually reclaimed and stops there. The old text promised
  // "start a new session to pick up where you left off", which nothing anywhere
  // does — a new session starts empty.
  "chatTop.idle.killedBody": {
    en: "We closed this session after a stretch of inactivity to free the workspace. Your work in the project is unaffected.",
    de: "Wir haben diese Sitzung nach längerer Inaktivität geschlossen und den Arbeitsbereich freigegeben. Deine Arbeit im Projekt ist davon unberührt.",
  },
  "chatTop.idle.restart": {
    en: "Start a new session",
    de: "Neue Sitzung starten",
  },

  // --- ModeToggle ---
  "chatTop.codingLocked": {
    en: "Coding mode unlocks once your project is ready.",
    de: "Der Coding-Modus geht erst, sobald dein Projekt bereit ist.",
  },
  "chatTop.modeGroupLabel": { en: "Conversation mode", de: "Konversationsmodus" },
  "chatTop.modePm": { en: "PM", de: "PM" },
  "chatTop.modeCoding": { en: "Coding", de: "Coding" },

  // --- Reset session ---
  "chatTop.resetConfirm": {
    en: "Reset this session?\n\nThis clears the chat history and the coding assistant's memory of this conversation, drops any message that hasn't been sent yet, and stops the current coding run. The link to this session still works — reopening it starts over with an empty conversation.",
    de: "Diese Sitzung zurücksetzen?\n\nDamit werden der Chatverlauf und das Gedächtnis des Coding-Assistenten für dieses Gespräch gelöscht, noch nicht gesendete Nachrichten verworfen und der laufende Coding-Durchlauf gestoppt. Der Link zu dieser Sitzung bleibt gültig — beim nächsten Öffnen startest du mit einer leeren Konversation.",
  },
  "chatTop.reset": { en: "Reset", de: "Zurücksetzen" },
  "chatTop.resetting": { en: "Resetting…", de: "Wird zurückgesetzt…" },
  "chatTop.resetTitle": {
    en: "Wipe this session's chat history and coding state. Same link stays valid.",
    de: "Chatverlauf und Coding-Zustand dieser Sitzung löschen. Der Link bleibt derselbe.",
  },
  "chatTop.newChat": { en: "New chat", de: "Neuer Chat" },
  "chatTop.newChatArmed": { en: "New chat — next message", de: "Neuer Chat — nächste Nachricht" },
  "chatTop.newChatTitle": {
    en: "Start a fresh conversation on your next message — keeps the chat history, resets what the assistant remembers.",
    de: "Beginnt mit deiner nächsten Nachricht eine frische Konversation — der Verlauf bleibt, das Gedächtnis des Assistenten wird zurückgesetzt.",
  },

  // --- WS connection status ---
  "chatTop.connecting": { en: "Connecting…", de: "Verbinden…" },
  "chatTop.connected": { en: "connected", de: "verbunden" },
  "chatTop.disconnected": { en: "disconnected", de: "getrennt" },
  "chatTop.reconnect": { en: "reconnect", de: "neu verbinden" },
  "chatTop.sessionExpired": { en: "session expired", de: "Sitzung abgelaufen" },
  "chatTop.accessDenied": { en: "access denied", de: "Zugriff verweigert" },

  // --- Header / banners ---
  "chatTop.switchProject": { en: "Switch project", de: "Projekt wechseln" },
  "chatTop.sessionInactive": {
    en: "Session is no longer active. Pick a project to start a new one.",
    de: "Diese Sitzung läuft nicht mehr. Wähle ein Projekt, um neu zu starten.",
  },
  // No "pick a project" call to action: the refusal is on the identity, so a
  // new session would be closed the same way.
  "chatTop.accessRevoked": {
    en: "You no longer have access to Loom. Ask an administrator to restore it.",
    de: "Du hast keinen Zugriff mehr auf Loom. Bitte eine Administratorin oder einen Administrator, ihn wiederherzustellen.",
  },
  "chatTop.conversationLabel": { en: "Conversation", de: "Konversation" },
  "chatTop.emptyTimelinePrefix": {
    en: "No messages yet. Type below to begin — e.g. ",
    de: "Noch keine Nachrichten. Schreib unten los — z. B. ",
  },

  // --- Branch / MR footer ---
  "chatTop.mrFailed": {
    en: "Couldn't create the merge request (error {status}). Try again.",
    de: "Merge Request konnte nicht erstellt werden (Fehler {status}). Versuch es erneut.",
  },
  "chatTop.commitsOn": { en: "on", de: "auf" },
  "chatTop.creatingMr": { en: "Creating MR…", de: "Merge Request wird erstellt…" },
  "chatTop.createMr": { en: "Create merge request", de: "Merge Request erstellen" },

  // --- Composer ---
  "chatTop.messagePlaceholder": { en: "Message Loom…", de: "Loom schreiben…" },
  "chatTop.messageLabel": { en: "Message", de: "Nachricht" },

  // --- Sign-in page ---
  "chatTop.signInTitle": { en: `Sign in to ${PUBLIC_PRODUCT_NAME}`, de: `Bei ${PUBLIC_PRODUCT_NAME} anmelden` },
  "chatTop.signInBlurb": {
    en: `${PUBLIC_PRODUCT_NAME} acts on issues and merge requests through your GitLab account.`,
    de: `${PUBLIC_PRODUCT_NAME} bearbeitet Issues und Merge Requests über dein GitLab-Konto.`,
  },
  "chatTop.signInInternal": {
    en: `${PUBLIC_PRODUCT_NAME} is an internal tool of Panter AG.`,
    de: `${PUBLIC_PRODUCT_NAME} ist ein internes Tool der Panter AG.`,
  },
  "chatTop.continueWithGitlab": { en: "Continue with GitLab", de: "Mit GitLab fortfahren" },
  // The review login (dev only, for an app review) below the sign-in button.
  "chatTop.reviewHeading": { en: "App review access", de: "Zugang für die App-Prüfung" },
  "chatTop.reviewHint": {
    en: "Only for app reviewers. Use the user name and password you were given.",
    de: "Nur für die App-Prüfung. Nimm den Benutzernamen und das Passwort, die du bekommen hast.",
  },
  "chatTop.reviewUsername": { en: "User name", de: "Benutzername" },
  "chatTop.reviewPassword": { en: "Password", de: "Passwort" },
  "chatTop.reviewSubmit": { en: "Sign in", de: "Anmelden" },
  "chatTop.reviewWrong": {
    en: "The user name or password is wrong.",
    de: "Benutzername oder Passwort stimmt nicht.",
  },
  "chatTop.reviewTooMany": {
    en: "Too many tries. Wait a minute, then try again.",
    de: "Zu viele Versuche. Warte eine Minute und versuch es dann noch einmal.",
  },
  "chatTop.reviewClosed": {
    en: "App review access has ended.",
    de: "Der Zugang für die App-Prüfung ist beendet.",
  },
  "chatTop.reviewFailed": {
    en: `${PUBLIC_PRODUCT_NAME} couldn't sign you in. Try again later.`,
    de: `${PUBLIC_PRODUCT_NAME} konnte dich nicht anmelden. Versuch es später noch einmal.`,
  },

  // --- Reauth banner ---
  "chatTop.reauthSignIn": { en: "Sign in to {host}", de: "Bei {host} anmelden" },
  "chatTop.reauthExpired": {
    en: "Your GitLab session expired. Sign in again to continue this conversation.",
    de: "Deine GitLab-Sitzung ist abgelaufen. Melde dich neu an, um hier weiterzumachen.",
  },

  // --- WS error timeline frames ---
  "chatTop.unknownIntent": { en: "Something went wrong showing part of this conversation.", de: "Beim Anzeigen eines Teils dieser Unterhaltung ist etwas schiefgelaufen." },
  "chatTop.protocolError": { en: "Something went wrong with the connection. Try refreshing the page.", de: "Bei der Verbindung ist etwas schiefgelaufen. Lade die Seite neu." },
};
